import {
  createRef,
  CSSProperties,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import classNames from 'classnames';
import ReactSelect, {
  ActionMeta,
  OptionTypeBase,
  Props as ReactSelectProps,
  ValueType,
} from 'react-select';

import { useClickEscape, useClickOutside } from '@dtci/triton/hooks';

import Button from '../Button';

import './Select.scss';

import { TestMetadata } from '../types';

type SelectProps<T extends OptionTypeBase> = ReactSelectProps<T> & {
  testMetadata?: {
    selectButtonDiv?: TestMetadata;
    selectButton?: TestMetadata;
    selectDropdown?: TestMetadata;
  };
};

interface ControlProps {
  children?: ReactNode;
}

const Control: FC<ControlProps> = (): JSX.Element => <div />;

interface DropDownProps {
  children: ReactNode;
  isOpen: boolean;
  target: ReactNode;
  expands?: boolean;
  testMetadata?: TestMetadata;
}

const Dropdown: FC<DropDownProps> = ({
  children,
  isOpen,
  target,
  testMetadata,
}): JSX.Element => (
  <div {...testMetadata}>
    {target}
    {isOpen ? <div>{children}</div> : null}
  </div>
);

const SelectButton = <
  T extends OptionTypeBase = {
    value: string;
    label: string;
    isDisabled?: boolean;
  },
>({
  className,
  styles,
  onChange,
  iconType,
  testMetadata = {
    selectButtonDiv: { 'data-testid': 'divSelectButton' },
    selectButton: { 'data-testid': 'selectButton' },
    selectDropdown: { 'data-testid': 'selectDropdown' },
  },
  ...otherProps
}: SelectProps<T>): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [value, setValue] = useState<ValueType<T, false>>(otherProps.value);

  useEffect(() => {
    setValue(otherProps.value);
  }, [otherProps.value]);

  const toggleOpen = useCallback(() => {
    setIsOpen(!isOpen);
  }, [setIsOpen, isOpen]);

  const onSelectChange = useCallback(
    (selection: ValueType<T, false>, action: ActionMeta<T>) => {
      setValue(selection);
      if (onChange) onChange(selection, action);
      toggleOpen();
    },
    [onChange, toggleOpen],
  );

  const props: SelectProps<T> = {
    ...otherProps,
    value: value,
    isSearchable: false,
    components: { Control: Control },
    styles: {
      ...styles,
      menu: (provided: CSSProperties) => ({
        ...provided,
        position: 'absolute',
        zIndex: 9,
        ...styles?.menu,
      }),
    },
    menuIsOpen: true,
    classNamePrefix: 'sb-select',
    className: classNames('sb-select-button', className),
    onChange: onSelectChange,
  };

  const dropDownRef = createRef<HTMLDivElement>();

  const closeIfOpen = (): void => {
    if (isOpen) {
      toggleOpen();
    }
  };

  useClickOutside(closeIfOpen, dropDownRef, [toggleOpen, dropDownRef]);

  useClickEscape(closeIfOpen, [toggleOpen]);

  return (
    <div ref={dropDownRef} {...testMetadata?.selectButtonDiv}>
      <Dropdown
        isOpen={isOpen}
        expands={otherProps.expands}
        testMetadata={testMetadata.selectDropdown}
        target={
          <Button
            iconPlacement="before"
            iconType={iconType}
            onClick={toggleOpen}
            className="sb-select-button__button"
            styleType="promoted"
            sizeType="small"
            {...testMetadata?.selectButton}
          >
            {value ? value.label : <>&nbsp;</>}
          </Button>
        }
      >
        <ReactSelect<T, false> {...props} />
      </Dropdown>
    </div>
  );
};

export default SelectButton;
